import { useCallback, useEffect, useRef, useState } from 'react';
import type { CardDepositResponse } from '../api/types';
import { useTranslation } from 'react-i18next';
import {
  PAYMENT_WIDGET_MESSAGE_TYPES,
  type PaymentWidgetMessage,
} from './types';
import { depositCardButtonSrcDoc } from '@/features/deposit-card/ui';

interface PaymentWidgetOptions {
  onOpen?: (idPayments: string) => void;
  onClose?: (idPayments: string) => void;
  onError?: (error: Error) => void;
}

export function usePaymentWidget({
  onOpen,
  onClose,
  onError,
}: PaymentWidgetOptions = {}) {
  const { t } = useTranslation();
  const [isWidgetOpen, setIsWidgetOpen] = useState(false);
  const [isPaymentWidgetLoading, setIsPaymentWidgetLoading] = useState(false);
  const [paymentWidgetError, setPaymentWidgetError] = useState<Error | null>(
    null
  );

  const containerRef = useRef<HTMLDivElement | null>(null);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const idPaymentsRef = useRef<string | null>(null);
  const observerRef = useRef<MutationObserver | null>(null);
  const loadTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const cleanupObserver = useCallback(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    if (loadTimeoutRef.current) {
      clearTimeout(loadTimeoutRef.current);
      loadTimeoutRef.current = null;
    }
  }, []);

  const closeWidget = useCallback(
    (shouldTriggerCallback = true) => {
      if (containerRef.current && iframeRef.current) {
        cleanupObserver();

        // Check if iframe is actually a child of document.body before removing
        if (document.body.contains(containerRef.current)) {
          try {
            document.body.removeChild(containerRef.current);
          } catch {
            // Iframe might already be removed
          }
        }

        containerRef.current = null;
        iframeRef.current = null;
        setIsWidgetOpen(false);

        if (shouldTriggerCallback) {
          onClose?.(idPaymentsRef.current || '');
        }
      }
    },
    [onClose, cleanupObserver]
  );

  const handleIframeError = useCallback(
    (errorMessage: string) => {
      const error = new Error(errorMessage);
      setPaymentWidgetError(error);
      onError?.(error);
      setIsPaymentWidgetLoading(false);
      closeWidget(false);
    },
    [onError, closeWidget]
  );

  // Listen for iframe loading issues and cross-origin messages
  useEffect(() => {
    const handleMessage = (event: MessageEvent<PaymentWidgetMessage>) => {
      // Handle messages from payment iframe if needed
      if (
        !iframeRef.current ||
        event.source !== iframeRef.current.contentWindow
      ) {
        return;
      }

      const eventData = event.data;

      console.debug('Payment widget message:', {
        type: eventData.type,
        payload: eventData.payload,
        message: eventData.message,
        idPayments: idPaymentsRef.current,
      });

      switch (eventData.type) {
        case PAYMENT_WIDGET_MESSAGE_TYPES.PAYMENT_ERROR: {
          const error = eventData.message || t('deposit.paymentGatewayError');
          handleIframeError(error);
          break;
        }

        case PAYMENT_WIDGET_MESSAGE_TYPES.PAYMENT_SUCCESS:
        case PAYMENT_WIDGET_MESSAGE_TYPES.WIDGET_CLOSE:
          closeWidget(true);
          break;

        default:
          console.debug('Unknown message type from widget:', eventData.type);
          break;
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [handleIframeError, closeWidget, t]);

  useEffect(() => {
    const handleBackMessage = (e: MessageEvent) => {
      if (e.data.type === 'BUTTON_IFRAME_BACK') {
        closeWidget(false);
      }
    };

    window.addEventListener('message', handleBackMessage);

    return () => {
      window.removeEventListener('message', handleBackMessage);
    };
  }, [closeWidget]);

  const openPaymentWidget = useCallback(
    (depositData: CardDepositResponse) => {
      try {
        if (iframeRef.current || isWidgetOpen) {
          closeWidget(false);
        }

        setIsPaymentWidgetLoading(true);
        setPaymentWidgetError(null);
        idPaymentsRef.current = depositData.idPayments;

        const existingIframes = document.querySelectorAll(
          'iframe[data-payment-widget="true"]'
        );
        existingIframes.forEach(iframe => {
          try {
            document.body.removeChild(iframe);
          } catch {
            console.log('Error removing existing iframe');
          }
        });

        const container = document.createElement('div');
        container.className = 'fixed top-0 z-9999 w-[100vw] h-[100vh]';

        const buttonIframe = document.createElement('iframe');
        buttonIframe.style.pointerEvents = 'auto';
        buttonIframe.srcdoc = depositCardButtonSrcDoc;
        buttonIframe.style.position = 'fixed';
        buttonIframe.style.top = '0';
        buttonIframe.style.width = '60px';
        buttonIframe.style.height = '60px';
        buttonIframe.style.zIndex = '9999';
        buttonIframe.style.display = 'none';

        const iframe = document.createElement('iframe');
        iframe.src = depositData.gatewayUrl;
        iframe.setAttribute('data-payment-widget', 'true');
        iframe.style.position = 'fixed';
        iframe.style.top = '0';
        iframe.style.left = '0';
        iframe.style.width = '100vw';
        iframe.style.height = '100vh';
        iframe.style.border = 'none';
        iframe.style.zIndex = '9998';
        iframe.style.pointerEvents = 'auto';
        iframe.allow = 'payment';
        iframe.allowFullscreen = true;

        container.appendChild(buttonIframe);
        container.appendChild(iframe);
        document.body.appendChild(container);

        containerRef.current = container;
        iframeRef.current = iframe;
        setIsWidgetOpen(true);

        // Set up timeout to detect frame-busting or loading issues
        loadTimeoutRef.current = setTimeout(() => {
          if (iframeRef.current && isPaymentWidgetLoading) {
            handleIframeError(
              t('deposit.paymentGatewayLoadError', {
                defaultValue:
                  'Payment gateway failed to load. The payment provider may not allow embedded payments.',
              })
            );
          }
        }, 10000);

        iframe.onload = () => {
          if (loadTimeoutRef.current) {
            clearTimeout(loadTimeoutRef.current);
            loadTimeoutRef.current = null;
          }

          setTimeout(() => {
            buttonIframe.style.display = 'initial';
          }, 1500);

          setIsPaymentWidgetLoading(false);
          onOpen?.(idPaymentsRef.current || '');
        };

        iframe.onerror = e => {
          console.error('Widget error', e);
          if (loadTimeoutRef.current) {
            clearTimeout(loadTimeoutRef.current);
            loadTimeoutRef.current = null;
          }
          handleIframeError(
            t('deposit.paymentGatewayError', {
              defaultValue:
                'Failed to load payment gateway. Please try again or contact support.',
            })
          );
        };

        const observer = new MutationObserver(mutations => {
          mutations.forEach(mutation => {
            if (mutation.type === 'childList') {
              mutation.removedNodes.forEach(node => {
                if (node === iframe) {
                  // Iframe was removed externally, cleanup and notify
                  observerRef.current = null;
                  setIsWidgetOpen(false);
                  onClose?.(idPaymentsRef.current || '');
                }
              });
            }
          });
        });

        observer.observe(document.body, { childList: true });
        observerRef.current = observer;
      } catch {
        if (loadTimeoutRef.current) {
          clearTimeout(loadTimeoutRef.current);
          loadTimeoutRef.current = null;
        }
        handleIframeError(
          t('deposit.paymentGatewayError', {
            defaultValue:
              'Failed to initialize payment gateway. Please try again or contact support.',
          })
        );
      }
    },
    [
      onOpen,
      onClose,
      onError,
      isWidgetOpen,
      closeWidget,
      handleIframeError,
      t,
      isPaymentWidgetLoading,
    ]
  );

  useEffect(() => {
    return () => {
      cleanupObserver();
      if (iframeRef.current) {
        try {
          document.body.removeChild(iframeRef.current);
        } catch {
          // Iframe might already be removed
        }
      }
    };
  }, [cleanupObserver]);

  return {
    openPaymentWidget,
    closeWidget: () => closeWidget(true),
    isWidgetOpen,
    isPaymentWidgetLoading,
    paymentWidgetError,
    idPayments: idPaymentsRef.current,
  };
}
